import {
  coachMemorySourceLabel,
  isUnconfirmedHypothesis,
  type CoachMemoryItem,
} from "@care-by-ella/contracts";

// TES-1211 — the short summary that leads each Patientminnet area.
//
// A-01: "Det ska primärt visa korta AI-sammanfattningar per coachrelevant område … i stället för en lång lista med formulärfrågor och svar. … Varje tematisk sammanfattning ska visa när den senast uppdaterades och kunna expanderas till de källor som ligger bakom"
//
// Simon (2026-10-18): built without the AI switch, so the summary is derived
// from the area's own rows and says nothing they do not: how many there
// are, the newest one quoted as it stands, when the area last changed and
// where its rows come from. It is computed on every read and stored
// nowhere, so a correction or inactivation of a row changes it at once.
// The rows themselves are the sources the area expands to, and they carry
// the confirm, correct and inactivate actions; the summary has no state of
// its own to confirm.

export interface MemoryAreaSummary {
  count: number;
  /** The newest row that is not an unconfirmed hypothesis, quoted as stored. */
  lead: CoachMemoryItem | null;
  /**
   * When the area last changed: the newest write (a new row, a correction,
   * which is a new row) or confirmation among its current rows.
   */
  lastUpdatedAt: string | null;
  /** Distinct "Källa" labels, newest row first. */
  sources: string[];
  /** Unconfirmed AI observations, never the lead. */
  unconfirmed: number;
}

export function summarizeMemoryArea(
  items: readonly CoachMemoryItem[],
): MemoryAreaSummary {
  const newestFirst = items
    .slice()
    .sort((a, b) => b.observedAt.localeCompare(a.observedAt));
  let lastUpdatedAt: string | null = null;
  for (const item of items) {
    for (const at of [item.createdAt, item.confirmedAt]) {
      if (at && (lastUpdatedAt === null || at > lastUpdatedAt)) {
        lastUpdatedAt = at;
      }
    }
  }
  return {
    count: items.length,
    lead: newestFirst.find((i) => !isUnconfirmedHypothesis(i)) ?? null,
    lastUpdatedAt,
    sources: [...new Set(newestFirst.map(coachMemorySourceLabel))],
    unconfirmed: items.filter(isUnconfirmedHypothesis).length,
  };
}

function fmtDay(iso: string): string {
  return new Date(iso).toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** The summary's facts line, e.g. "3 noteringar · Senast uppdaterad 2 okt. 2026 · Källa: Chatt". */
export function memoryAreaFacts(summary: MemoryAreaSummary): string {
  const parts = [
    summary.count === 1 ? "1 notering" : `${summary.count} noteringar`,
  ];
  if (summary.lastUpdatedAt) {
    parts.push(`Senast uppdaterad ${fmtDay(summary.lastUpdatedAt)}`);
  }
  if (summary.sources.length > 0) {
    parts.push(
      `${summary.sources.length === 1 ? "Källa" : "Källor"}: ${summary.sources.join(", ")}`,
    );
  }
  return parts.join(" · ");
}

/** The line under the lead about unconfirmed AI observations, or null. */
export function memoryAreaUnconfirmedLine(
  summary: MemoryAreaSummary,
): string | null {
  if (summary.unconfirmed === 0) return null;
  const what =
    summary.unconfirmed === 1
      ? "1 obekräftad AI-observation"
      : `${summary.unconfirmed} obekräftade AI-observationer`;
  return summary.lead
    ? `Dessutom ${what} att bekräfta bland källorna.`
    : `Bara ${what} – öppna källorna för att bekräfta eller ta bort.`;
}
